/**
 * Notification sound composable.
 *
 * Plays a short, subtle audio cue (a "ding") to draw attention to incoming
 * SimChat messages/notifications while the chat drawer is closed. This is an
 * *additional* signal on top of the existing visual unread badge — never the
 * only way information is conveyed (WCAG 2.1 AA, SC 1.4.1 / 1.4.2).
 *
 * Design notes:
 * - The cue is generated with the Web Audio API rather than shipping an audio
 *   asset, keeping it tiny and guaranteeing it stays well under the WCAG
 *   3-second auto-play threshold (the tone here is ~0.3s).
 * - Sound is on by default but can be muted by the user. The preference is
 *   persisted in localStorage so it survives reloads.
 * - Playback is throttled so a burst of messages produces a single ding
 *   rather than a rapid stack of them.
 *
 * Usage:
 *   const { playDing, muted, toggleMuted } = useNotificationSound()
 *   playDing() // throttled, respects mute + browser autoplay policy
 */
import { ref } from 'vue'

const STORAGE_KEY = 'chartflow.simchat.soundMuted'
// Minimum gap between dings. A burst of messages within this window yields
// a single sound rather than one per message.
const THROTTLE_MS = 2000

function readMutedPreference() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    // localStorage can throw in private mode / sandboxed iframes — default to on.
    return false
  }
}

function writeMutedPreference(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false')
  } catch {
    // Non-fatal: the in-memory ref still reflects the user's choice for the session.
  }
}

/**
 * Given a list of newly-added chat messages and the current user's uid,
 * return true if at least one message originated from someone else.
 *
 * Own outgoing chat messages are echoed back by the server, so they must be
 * excluded to avoid playing the cue for yourself. System/notification messages
 * have no sender_embed and are always treated as incoming.
 */
export function hasIncomingFromOthers(addedMessages, currentUid) {
  if (!Array.isArray(addedMessages)) return false
  return addedMessages.some((msg) => {
    const senderUid = msg && msg.sender_embed && msg.sender_embed.uid
    return !senderUid || senderUid !== currentUid
  })
}

export function useNotificationSound() {
  const muted = ref(readMutedPreference())
  let lastPlayed = 0
  let audioCtx = null

  function getAudioContext() {
    if (typeof window === 'undefined') return null
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return null
    if (!audioCtx) {
      try {
        audioCtx = new Ctx()
      } catch {
        // WebKit can throw InvalidStateError ("Failed to start the audio
        // device") when the platform audio session fails to initialize —
        // an OS/browser condition outside the app's control. The audio cue
        // is a supplementary signal on top of the visual unread badge, so
        // treat this the same as the API being unavailable.
        return null
      }
    }
    return audioCtx
  }

  /**
   * Synthesize a short two-tone "ding" using the Web Audio API.
   * Kept brief (~0.3s) and at a modest gain so it's noticeable but not jarring.
   */
  function emitTone() {
    const ctx = getAudioContext()
    if (!ctx) return false

    // Browsers may start the context in a "suspended" state until a user
    // gesture occurs. resume() is a no-op if already running.
    if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
      ctx.resume()
    }

    const now = ctx.currentTime
    const gain = ctx.createGain()
    gain.connect(ctx.destination)
    // Gentle attack/decay envelope, peak well below full scale.
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(0.15, now + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3)

    const osc = ctx.createOscillator()
    osc.type = 'sine'
    // Two-note rising chime (A5 -> E6).
    osc.frequency.setValueAtTime(880, now)
    osc.frequency.setValueAtTime(1318.5, now + 0.12)
    osc.connect(gain)
    osc.start(now)
    osc.stop(now + 0.32)
    return true
  }

  /**
   * Play the notification cue if not muted and not throttled.
   * Returns true if a sound was actually emitted.
   */
  function playDing() {
    if (muted.value) return false
    const now = Date.now()
    if (now - lastPlayed < THROTTLE_MS) return false
    const played = emitTone()
    if (played) lastPlayed = now
    return played
  }

  function setMuted(value) {
    muted.value = !!value
    writeMutedPreference(muted.value)
  }

  function toggleMuted() {
    setMuted(!muted.value)
  }

  return {
    muted,
    playDing,
    setMuted,
    toggleMuted,
  }
}
